import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, type Ref } from 'react';
import { Box, Collapse, FormControlLabel, IconButton, Paper, Switch, Tooltip, Typography } from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useApiStore, useAuthStore, useSettingsStore } from '@core/store';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { ActiveLicenses, DeviceModule } from '@core/types';
import { ModuleKind } from '@features/shopautomation/types.ts';
import { nameValidationRules, notNegative, required } from '@core/utils/validation.ts';
import TrolleyShelterLocationImageEditor from '@features/administration/components/trolleyshelter/TrolleyShelterLocationImageEditor.tsx';
import ToggleButton from '@/components/shared/ToggleButton.tsx';
import ZoneGatesFields from '@features/administration/components/shopSetup/ZoneGatesFields.tsx';
import LineGatesFields from '@features/administration/components/shopSetup/LineGatesFields.tsx';
import ActiveOperationModesFields from '@features/administration/components/shopSetup/ActiveOperationModesFields.tsx';
import AccessConfigurationsFields from '@features/administration/components/shopSetup/AccessConfigurationsFields.tsx';
import { FormContainer, WcnButton, WcnTextField } from '@/components';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { v4 as uuidv4 } from 'uuid';
import { moduleService, logicServiceManagementService, LogicServiceLayout } from '@/services';
import { defaultOperationModes } from '@features/administration/components/operationModesSchedules/scheduleDisplay.ts';

const ExpandableFieldSection: React.FC<{
    title: string;
    expanded: boolean;
    onToggle: () => void;
    isMobile: boolean;
    children: React.ReactNode;
}> = ({ title, expanded, onToggle, isMobile, children }) => {
    const headerMarginLeft = isMobile ? 16 - 10 : 16 - 180;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: `${headerMarginLeft}px` }}>
                <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }}>
                    <ExpandMore
                        sx={{
                            fontSize: 20,
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }}
                    />
                </IconButton>
                <Typography
                    variant="subtitle2"
                    onClick={onToggle}
                    sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    {title}
                </Typography>
                <Tooltip title={title} placement="right" slotProps={{ tooltip: { className: 'page-title-tooltip' } }}>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                        <HelpOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
                <Box sx={{ pt: 2 }}>{children}</Box>
            </Collapse>
        </Box>
    );
};

interface AddSoftwareModulePanelProps {
    shopId: string;
    initialModule?: DeviceModule | null;
    onCancel: () => void;
    onChanged: () => void;
    ref?: Ref<AddSoftwareModulePanelRef>;
}

export interface AddSoftwareModulePanelRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}

export interface ComponentId {
    id: string;
    shopId: string;
    name: string;
    discriminator: string;
}

const ZONE_TYPES = [
    { type: 'virtual', value: 1, textKey: 'virtual' },
    { type: 'native', value: 2, textKey: 'native' }
];

const isVirtualZone = (zoneTypeValue: number | undefined) => {
    return ZONE_TYPES.find((x) => x.type === 'virtual')?.value === zoneTypeValue;
};

const MAX_DWELL_TIME_OPTIONS = [15, 30, 45, 60, 90];

let logicServiceConfigsCache: LogicServiceLayout[] | null = null;

const AddSoftwareModulePanel: React.FC<AddSoftwareModulePanelProps> = ({
    shopId,
    initialModule,
    onCancel,
    onChanged,
    ref
}) => {
    const { t } = useTranslation();
    const { hasLicense } = useAuthStore();
    const { isMobile } = useSettingsStore();
    const { notifyErrorResponse, notifySuccess, notifyWarning } = useNotifications();
    const [logicServiceConfigs, setLogicServiceConfigs] = useState<LogicServiceLayout[]>([]);

    const isEditMode = !!initialModule;

    const [isCreating, setIsCreating] = useState(false);
    const [module, setModule] = useState<DeviceModule>(initialModule ? { ...initialModule } : { kind: '', id: '' });
    const initialModuleRef = useRef<DeviceModule>(initialModule ? { ...initialModule } : { kind: '', id: '' });

    const moduleTypes = [
        {
            value: 'TrolleyShelter',
            label: t('trolleyShelter'),
            show: hasLicense(ActiveLicenses.Rfid)
        },
        {
            value: 'ZoneTracker',
            label: t('zoneTracker'),
            show: hasLicense(ActiveLicenses.ShopAutomation)
        },
        {
            value: 'LineTracker',
            label: t('lineTracker'),
            show: hasLicense(ActiveLicenses.ShopAutomation)
        },
        {
            value: 'ShopAutomation',
            label: t('shopAutomation'),
            show: hasLicense(ActiveLicenses.ShopAutomation)
        }
    ].filter((type) => type.show || type.value === module.kind);

    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);
    const [enableOperationModesSchedules, setEnableOperationModesSchedules] = useState(false);
    const [uploadPictureExpanded, setUploadPictureExpanded] = useState(true);
    const [zoneGatesExpanded, setZoneGatesExpanded] = useState(true);
    const [lineGatesExpanded, setLineGatesExpanded] = useState(true);
    const [activeOperationModesExpanded, setActiveOperationModesExpanded] = useState(true);
    const [accessConfigurationsExpanded, setAccessConfigurationsExpanded] = useState(true);
    const {
        gatewayIds,
        loadGatewayIds,
        componentIds,
        loadComponentIds,
        operationModes,
        loadOperationModes,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds,
        loadShopAutomationIds
    } = useApiStore();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            await Promise.all([loadGatewayIds(), loadComponentIds(), loadOperationModes(), loadLogicServiceConfigs()]);
        } catch (error) {
            notifyErrorResponse(error);
        }
    };

    const loadLogicServiceConfigs = async () => {
        if (logicServiceConfigsCache) {
            setLogicServiceConfigs(logicServiceConfigsCache);
            return;
        }

        try {
            const { layouts } = await logicServiceManagementService.loadAll();
            logicServiceConfigsCache = layouts || [];
            setLogicServiceConfigs(logicServiceConfigsCache);
        } catch (error) {
            notifyErrorResponse(error);
        }
    };

    const shopGatewayIds = gatewayIds.filter((g) => g.shopId === shopId);
    const shopImpinjRfidReaderIds = componentIds.filter(
        (c) => c.shopId === shopId && c.discriminator === 'ImpinjRfidReader'
    );

    const capacityRules = () => {
        return (value: any) => {
            if (value === '' || value === null || value === undefined) return t('required');
            if (value < 0) return t('gtZero');
            return '';
        };
    };

    const handleModuleTypeSelect = (kind: ModuleKind) => {
        // @ts-ignore
        const newModule: DeviceModule = { kind };

        switch (kind) {
            case 'Gateway':
                newModule.shopId = shopId;
                newModule.deviceId = null;
                newModule.disabled = false;
                break;
            case 'TrolleyShelter':
                newModule.id = uuidv4();
                newModule.gateway = null;
                newModule.moduleId = null;
                newModule.disabled = false;
                newModule.description = '';
                newModule.capacity = 0;
                newModule.impinjRfidReaderId = null;
                break;
            case 'ZoneTracker':
                newModule.gateway = null;
                newModule.moduleId = null;
                newModule.disabled = false;
                newModule.zoneType = 1;
                newModule.intervalInMinutes = 1;
                newModule.maxDwellTimeMinutes = 15;
                newModule.resetAfterInactivityMinutes = 90;
                newModule.zoneGates = [
                    {
                        sensorCameraId: null,
                        elementId: null,
                        isReversed: false
                    }
                ];
                break;
            case 'LineTracker':
                newModule.gateway = null;
                newModule.moduleId = null;
                newModule.disabled = false;
                newModule.intervalInMinutes = 1;
                newModule.lineGates = [
                    {
                        sensorCameraId: null,
                        elementId: null,
                        elementName: '',
                        isReversed: false
                    }
                ];
                break;
            case 'ShopAutomation':
                newModule.gateway = null;
                newModule.moduleId = '';
                newModule.disabled = false;
                newModule.activeOperationModes = defaultOperationModes;
                newModule.operationModesSchedules = [];
                newModule.isOperationModeScheduleActive = false;
                newModule.hasDisabledOperationModeSchedules = false;
                newModule.incidentNotifications = [];
                newModule.accessConfigurations = [];
                newModule.logicServiceConfigId = null;
                break;
            default:
                break;
        }

        setModule(newModule);
    };

    const handleFieldChange = useCallback((field: string, value: any) => {
        setModule((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleSave = async (): Promise<boolean> => {
        const isValid = formRef.current ? formRef.current.validate() : formValid;
        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }

        setIsCreating(true);
        try {
            const payload = {
                ...module,
                gatewayId: module.gateway?.id,
                hasDisabledOperationModeSchedules: enableOperationModesSchedules
                    ? module.hasDisabledOperationModeSchedules
                    : true
            } as DeviceModule;

            if (isEditMode) {
                await moduleService.updateModule(module.kind, module.id, payload);
            } else {
                await moduleService.createModule(payload);
            }
            notifySuccess();
            await reloadModuleIds(module.kind);
            onChanged();
            return true;
        } catch (error: any) {
            if (!isEditMode && module.kind === 'Gateway' && error?.response?.status === 409) {
                await reloadModuleIds(module.kind);
                onChanged();
                return true;
            } else {
                notifyErrorResponse(error);
                return false;
            }
        } finally {
            setIsCreating(false);
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            isDirty: JSON.stringify(module) !== JSON.stringify(initialModuleRef.current),
            save: handleSave
        }),
        [module, formValid]
    );

    const reloadModuleIds = async (kind: string) => {
        switch (kind) {
            case 'Gateway':
                await loadGatewayIds();
                break;
            case 'TrolleyShelter':
                await loadTrolleyShelterIds();
                break;
            case 'ZoneTracker':
                await loadZoneTrackerIds();
                break;
            case 'LineTracker':
                await loadLineTrackerIds();
                break;
            case 'ShopAutomation':
                await loadShopAutomationIds();
                break;
        }
    };

    const renderModuleFields = () => {
        switch (module.kind) {
            case 'Gateway':
                return (
                    <>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'deviceId'}
                                    label={t('name')}
                                    value={module.deviceId || ''}
                                    onChange={(e) => handleFieldChange('deviceId', e.target.value)}
                                    required
                                    fullWidth
                                    rules={nameValidationRules(t)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!module.disabled}
                                            onChange={(e) => setModule({ ...module, disabled: !e.target.checked })}
                                        />
                                    }
                                    label={module.disabled ? t('disabled') : t('enabled')}
                                />
                            </div>
                        </div>
                    </>
                );

            case 'TrolleyShelter':
                return (
                    <>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'gateway'}
                                    label={t('gateway')}
                                    value={module.gateway?.id || ''}
                                    items={shopGatewayIds.map((item) => ({ id: item.id, name: item.deviceId }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => {
                                        const selectedGateway = shopGatewayIds.find((g) => g.id === val);
                                        console.log('selected gateway:', selectedGateway);
                                        setModule({ ...module, gateway: (selectedGateway as any) || null });
                                    }}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ToggleButton
                                    checked={!module.disabled}
                                    onChange={(value) => setModule({ ...module, disabled: !value })}
                                    label={module.disabled ? t('disabled') : t('enabled')}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'moduleId'}
                                    label={t('name')}
                                    value={module.moduleId || ''}
                                    onChange={(e) => handleFieldChange('moduleId', e.target.value)}
                                    required
                                    fullWidth
                                    rules={nameValidationRules(t)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <WcnTextField
                                    label={t('description')}
                                    value={module.description || ''}
                                    onChange={(e) => setModule({ ...module, description: e.target.value })}
                                    fullWidth
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'capacity'}
                                    label={t('capacity')}
                                    type="number"
                                    value={module.capacity || 0}
                                    onChange={(e) => handleFieldChange('capacity', parseInt(e.target.value))}
                                    required
                                    fullWidth
                                    rules={[capacityRules()]}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'impinjRfidReaderId'}
                                    label={'Impinj RFID Reader'}
                                    value={module.impinjRfidReaderId || ''}
                                    items={shopImpinjRfidReaderIds.map((item) => ({ id: item.id, name: item.name }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => setModule({ ...module, impinjRfidReaderId: val })}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ExpandableFieldSection
                                    title={t('uploadPicture')}
                                    expanded={uploadPictureExpanded}
                                    onToggle={() => setUploadPictureExpanded((prev) => !prev)}
                                    isMobile={isMobile}
                                >
                                    <Box sx={{ width: '49%' }}>
                                        <TrolleyShelterLocationImageEditor
                                            module={module}
                                            showActions={false}
                                            onImageChange={(imagePath) =>
                                                setModule({ ...module, imagePath: imagePath ?? undefined })
                                            }
                                        />
                                    </Box>
                                </ExpandableFieldSection>
                            </div>
                        </div>
                    </>
                );

            case 'ZoneTracker':
                return (
                    <>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'gateway'}
                                    label={t('gateway')}
                                    value={module.gateway?.id || ''}
                                    items={shopGatewayIds.map((item) => ({ id: item.id, name: item.deviceId }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => {
                                        const selectedGateway = shopGatewayIds.find((g) => g.id === val);
                                        console.log('selected gateway:', selectedGateway);
                                        setModule({ ...module, gateway: (selectedGateway as any) || null });
                                    }}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'moduleId'}
                                    label={t('name')}
                                    value={module.moduleId || ''}
                                    onChange={(e) => setModule({ ...module, moduleId: e.target.value })}
                                    required
                                    fullWidth
                                    rules={nameValidationRules(t)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'zoneType'}
                                    label={t('zoneType')}
                                    value={module?.zoneType || ''}
                                    items={ZONE_TYPES.map((type) => ({
                                        value: type.value,
                                        text: t(type.textKey)
                                    }))}
                                    itemText="text"
                                    itemValue="value"
                                    onChange={(e) => setModule({ ...module, zoneType: e })}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'intervalInMinutes'}
                                    label={`${t('interval')} (${t('minutes')})`}
                                    type="number"
                                    value={module.intervalInMinutes || ''}
                                    onChange={(e) =>
                                        setModule({ ...module, intervalInMinutes: Number(e.target.value) })
                                    }
                                    required
                                    fullWidth
                                    rules={[required(t)]}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'maxDwellTimeMinutes'}
                                    label={`${t('maxDwellTime')} (${t('minutes')} - ${t('sensorCamera')})`}
                                    value={module?.maxDwellTimeMinutes || ''}
                                    items={MAX_DWELL_TIME_OPTIONS.map((item) => ({ value: item }))}
                                    itemText="value"
                                    itemValue="value"
                                    onChange={(e) => setModule({ ...module, maxDwellTimeMinutes: e })}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        {isVirtualZone(module.zoneType) && (
                            <div className="form-row">
                                <div className="form-col form-col-12 form-col-6">
                                    <FormTextField
                                        name={'resetAfterInactivityMinutes'}
                                        label={`${t('resetAfterInactivity')} ${t('minutes')}`}
                                        type="number"
                                        value={module.resetAfterInactivityMinutes || ''}
                                        onChange={(e) =>
                                            setModule({
                                                ...module,
                                                resetAfterInactivityMinutes: Number(e.target.value)
                                            })
                                        }
                                        required
                                        fullWidth
                                        rules={[required(t), notNegative(t)]}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ExpandableFieldSection
                                    title={t('zoneGates')}
                                    expanded={zoneGatesExpanded}
                                    onToggle={() => setZoneGatesExpanded((prev) => !prev)}
                                    isMobile={isMobile}
                                >
                                    <ZoneGatesFields
                                        value={module.zoneGates || []}
                                        shopId={shopId}
                                        zoneType={module.zoneType}
                                        onChange={(gates) => setModule({ ...module, zoneGates: gates })}
                                    />
                                </ExpandableFieldSection>
                            </div>
                        </div>
                    </>
                );
            case 'LineTracker':
                return (
                    <>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'gateway'}
                                    label={t('gateway')}
                                    value={module.gateway?.id || ''}
                                    items={shopGatewayIds.map((item) => ({ id: item.id, name: item.deviceId }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => {
                                        const selectedGateway = shopGatewayIds.find((g) => g.id === val);
                                        setModule({ ...module, gateway: (selectedGateway as any) || null });
                                    }}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!module.disabled}
                                            onChange={(e) => setModule({ ...module, disabled: !e.target.checked })}
                                        />
                                    }
                                    label={module.disabled ? t('disabled') : t('enabled')}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'moduleId'}
                                    label={t('moduleName')}
                                    value={module.moduleId || ''}
                                    onChange={(e) => setModule({ ...module, moduleId: e.target.value })}
                                    required
                                    fullWidth
                                    rules={nameValidationRules(t)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'intervalInMinutes'}
                                    label={`${t('interval')} (${t('minutes')})`}
                                    type="number"
                                    value={module.intervalInMinutes || ''}
                                    onChange={(e) =>
                                        setModule({ ...module, intervalInMinutes: Number(e.target.value) })
                                    }
                                    required
                                    fullWidth
                                    rules={[required(t)]}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ExpandableFieldSection
                                    title={t('hardware')}
                                    expanded={lineGatesExpanded}
                                    onToggle={() => setLineGatesExpanded((prev) => !prev)}
                                    isMobile={isMobile}
                                >
                                    <LineGatesFields
                                        value={module.lineGates || []}
                                        shopId={shopId}
                                        onChange={(lines) => setModule({ ...module, lineGates: lines })}
                                    />
                                </ExpandableFieldSection>
                            </div>
                        </div>
                    </>
                );
            case 'ShopAutomation':
                return (
                    <>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'gateway'}
                                    label={t('gateway')}
                                    value={module.gateway?.id || ''}
                                    items={shopGatewayIds.map((item) => ({ id: item.id, name: item.deviceId }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => {
                                        const selectedGateway = shopGatewayIds.find((g) => g.id === val);
                                        setModule({ ...module, gateway: (selectedGateway as any) || null });
                                    }}
                                    rules={[required(t)]}
                                    required
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!module.disabled}
                                            onChange={(e) => setModule({ ...module, disabled: !e.target.checked })}
                                        />
                                    }
                                    label={module.disabled ? t('disabled') : t('enabled')}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormTextField
                                    name={'moduleId'}
                                    label={t('name')}
                                    value={module.moduleId || ''}
                                    onChange={(e) => setModule({ ...module, moduleId: e.target.value })}
                                    required
                                    fullWidth
                                    rules={nameValidationRules(t)}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12 form-col-6">
                                <FormSelect
                                    name={'logicServiceConfig'}
                                    label={t('logicServiceConfig')}
                                    value={module.logicServiceConfigId || ''}
                                    items={logicServiceConfigs.map((item) => ({ id: item.id, name: item.name }))}
                                    itemText="name"
                                    itemValue="id"
                                    onChange={(val) => setModule({ ...module, logicServiceConfigId: val })}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ExpandableFieldSection
                                    title={t('activeOperationModes')}
                                    expanded={activeOperationModesExpanded}
                                    onToggle={() => setActiveOperationModesExpanded((prev) => !prev)}
                                    isMobile={isMobile}
                                >
                                    <ActiveOperationModesFields
                                        operationModes={operationModes}
                                        activeOperationModes={module.activeOperationModes || []}
                                        onActiveOperationModesChange={(modes) =>
                                            setModule({ ...module, activeOperationModes: modes })
                                        }
                                    />
                                </ExpandableFieldSection>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-col form-col-12">
                                <ExpandableFieldSection
                                    title={t('accessConfigurations')}
                                    expanded={accessConfigurationsExpanded}
                                    onToggle={() => setAccessConfigurationsExpanded((prev) => !prev)}
                                    isMobile={isMobile}
                                >
                                    <AccessConfigurationsFields
                                        value={module.accessConfigurations || []}
                                        activeOperationModes={module.activeOperationModes || []}
                                        onChange={(accessConfigurations) =>
                                            setModule({
                                                ...module,
                                                accessConfigurations: accessConfigurations
                                            })
                                        }
                                    />
                                </ExpandableFieldSection>
                            </div>
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: '14px' }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                        {isEditMode ? t('editModule') : t('addModule')}
                    </Typography>
                    <Tooltip
                        title={isEditMode ? t('editModule') : t('addModule')}
                        placement="right"
                        slotProps={{ tooltip: { className: 'page-title-tooltip' } }}
                    >
                        <IconButton size="small" sx={{ p: 0.25 }}>
                            <HelpOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ padding: isMobile ? '24px 10px' : '42px 100px 24px 180px' }}>
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <div className="form-row">
                        <div className="form-col form-col-12 form-col-6">
                            <FormSelect
                                name={'moduleType'}
                                label={t('moduleType')}
                                value={module.kind || ''}
                                items={moduleTypes.map((type) => ({ value: type.value, text: type.label }))}
                                itemText="text"
                                itemValue="value"
                                onChange={(val) => handleModuleTypeSelect(val as ModuleKind)}
                                rules={[required(t)]}
                                required
                                placeholder={t('selectModuleType')}
                                disabled={isEditMode}
                                sx={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    {renderModuleFields()}
                </FormContainer>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        alignItems: isMobile ? 'center' : 'unset',
                        gap: 1,
                        mt: 3
                    }}
                >
                    <WcnButton variant="red" onClick={onCancel} style={isMobile ? { width: '270px' } : undefined}>
                        {t('cancel')}
                    </WcnButton>
                    <WcnButton
                        variant="primary"
                        onClick={handleSave}
                        disabled={isCreating || !formValid}
                        style={isMobile ? { width: '270px' } : undefined}
                    >
                        {t('save')}
                    </WcnButton>
                </Box>

                <LoadingOverlay open={isCreating} />
            </Box>
        </Paper>
    );
};

export default AddSoftwareModulePanel;
